import { Controller } from "@hotwired/stimulus"

// Handles drag-and-drop file uploads for document cards
// Provides visual feedback during drag and triggers the existing upload flow on drop
//
// Usage:
//   On the document card element:
//     data-controller="tax-organizer--document-drop"
//
//   On the file input (target):
//     data-tax-organizer--document-drop-target="fileInput"
//
export default class extends Controller {
  static targets = ["fileInput"]

  connect() {
    this.dragCounter = 0
    this.boundHandleDragEnter = this.handleDragEnter.bind(this)
    this.boundHandleDragOver = this.handleDragOver.bind(this)
    this.boundHandleDragLeave = this.handleDragLeave.bind(this)
    this.boundHandleDrop = this.handleDrop.bind(this)

    this.element.addEventListener("dragenter", this.boundHandleDragEnter)
    this.element.addEventListener("dragover", this.boundHandleDragOver)
    this.element.addEventListener("dragleave", this.boundHandleDragLeave)
    this.element.addEventListener("drop", this.boundHandleDrop)
  }

  disconnect() {
    this.element.removeEventListener("dragenter", this.boundHandleDragEnter)
    this.element.removeEventListener("dragover", this.boundHandleDragOver)
    this.element.removeEventListener("dragleave", this.boundHandleDragLeave)
    this.element.removeEventListener("drop", this.boundHandleDrop)
  }

  handleDragEnter(event) {
    event.preventDefault()
    this.dragCounter++

    if (this.dragCounter === 1) {
      this.showDropZone()
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    // Required to allow drop
    event.dataTransfer.dropEffect = "copy"
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dragCounter--

    if (this.dragCounter === 0) {
      this.hideDropZone()
    }
  }

  handleDrop(event) {
    event.preventDefault()
    this.dragCounter = 0
    this.hideDropZone()

    const files = event.dataTransfer.files
    if (files.length === 0) return

    if (!this.hasFileInputTarget) {
      this.showError("Maximum files reached for this document")
      return
    }

    if (this.isUploading()) {
      this.showError("Upload already in progress")
      return
    }

    const acceptedFormats = this.fileInputTarget.accept
    const validFiles = []
    const invalidFiles = []

    for (const file of files) {
      if (this.isValidFileType(file, acceptedFormats)) {
        validFiles.push(file)
      } else {
        invalidFiles.push(file.name)
      }
    }

    if (validFiles.length === 0) {
      this.showError(`Invalid file type(s). Accepted formats: ${acceptedFormats}`)
      return
    }

    if (invalidFiles.length > 0) {
      // Some files invalid, but we have valid ones - warn but continue
      console.warn(`Skipped invalid files: ${invalidFiles.join(", ")}`)
    }

    this.triggerUpload(validFiles)
  }

  showDropZone() {
    if (!this.hasFileInputTarget) {
      // No file input means max uploads reached - show disabled state
      this.element.classList.add("drag-over-disabled")
    } else if (this.isUploading()) {
      this.element.classList.add("drag-over-disabled")
    } else {
      this.element.classList.add("drag-over")
    }
  }

  hideDropZone() {
    this.element.classList.remove("drag-over", "drag-over-disabled")
  }

  isUploading() {
    return this.element.hasAttribute("data-saving")
  }

  isValidFileType(file, acceptedFormats) {
    if (!acceptedFormats) return true

    const formats = acceptedFormats.split(",").map(f => f.trim().toLowerCase())
    const fileName = file.name.toLowerCase()
    const fileExtension = "." + fileName.split(".").pop()

    return formats.some(format => {
      if (format.startsWith(".")) {
        return fileExtension === format
      }
      // Handle MIME types if needed
      return file.type === format
    })
  }

  triggerUpload(files) {
    // Create a DataTransfer to set files on the input
    const dataTransfer = new DataTransfer()
    for (const file of files) {
      dataTransfer.items.add(file)
    }
    this.fileInputTarget.files = dataTransfer.files

    // Dispatch change event to trigger the existing upload controller
    const changeEvent = new Event("change", { bubbles: true })
    this.fileInputTarget.dispatchEvent(changeEvent)
  }

  showError(message) {
    // Brief visual feedback - flash the card red
    this.element.classList.add("drag-over-error")
    setTimeout(() => {
      this.element.classList.remove("drag-over-error")
    }, 1000)

    // Also show an alert for now - can be replaced with toast later
    alert(message)
  }
}
